import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "../lib/api";
import { useAuth } from "./LoginGate";
import { canWrite } from "../lib/constants";

export interface Followup {
  id: number;
  case_id: number;
  text: string;
  due_at: string | null;
  completed_at: string | null;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
}

function formatDate(d: string | null) {
  return d ? new Date(d).toLocaleDateString("en-GB") : "—";
}

function dueBadgeClass(dueAt: string | null, completed: boolean) {
  if (completed) return "badge badge-gray";
  if (!dueAt) return "badge badge-blue";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueAt);
  due.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (days < 0) return "badge badge-red";
  if (days <= 3) return "badge badge-yellow";
  return "badge badge-blue";
}

export default function FollowupsPanel({
  caseId,
  caseClosed,
  onChange,
}: {
  caseId: number;
  caseClosed: boolean;
  onChange?: () => void;
}) {
  const { user } = useAuth();
  const writable = canWrite(user?.role) && !caseClosed;
  const [items, setItems] = useState<Followup[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/cases/${caseId}/followups`);
      if (r.ok) setItems(await r.json());
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    setError("");
    try {
      const r = await apiFetch(`/api/cases/${caseId}/followups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.trim(), due_at: dueAt || null }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data.error || "Failed to add follow-up");
        return;
      }
      setText("");
      setDueAt("");
      await load();
      onChange?.();
    } finally {
      setSaving(false);
    }
  }

  async function toggleComplete(f: Followup) {
    await apiFetch(`/api/followups/${f.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ completed: !f.completed_at }),
    });
    await load();
    onChange?.();
  }

  async function remove(f: Followup) {
    if (!confirm("Delete this follow-up?")) return;
    await apiFetch(`/api/followups/${f.id}`, { method: "DELETE" });
    await load();
    onChange?.();
  }

  const open = items.filter(i => !i.completed_at);
  const done = items.filter(i => !!i.completed_at);

  return (
    <div>
      {writable && (
        <form onSubmit={handleAdd} style={{ background: "var(--bg-light)", borderRadius: 8, padding: 12, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Add Follow-up</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <textarea
              className="form-textarea"
              placeholder="What needs following up?"
              value={text}
              onChange={e => setText(e.target.value)}
              rows={2}
              required
            />
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <label className="form-label" style={{ margin: 0, fontSize: 11 }}>Deadline (optional)</label>
                <input
                  className="form-input"
                  type="date"
                  value={dueAt}
                  onChange={e => setDueAt(e.target.value)}
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !text.trim()}
              >
                {saving ? "Adding…" : "Add Follow-up"}
              </button>
            </div>
            {error && <div style={{ color: "var(--danger)", fontSize: 12 }}>{error}</div>}
          </div>
        </form>
      )}

      {!writable && caseClosed && (
        <div style={{ background: "var(--bg-light)", borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 12, color: "var(--text-muted)" }}>
          Follow-ups are disabled for closed cases.
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>Loading…</div>
      ) : (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 8 }}>
            OPEN ({open.length})
          </div>
          {open.length === 0 && (
            <div style={{ padding: 16, textAlign: "center", color: "var(--text-muted)", fontSize: 13, background: "var(--bg-light)", borderRadius: 8, marginBottom: 12 }}>
              No open follow-ups.
            </div>
          )}
          {open.map(f => (
            <div
              key={f.id}
              style={{
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "10px 12px",
                marginBottom: 8,
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              {writable && (
                <input
                  type="checkbox"
                  checked={false}
                  onChange={() => toggleComplete(f)}
                  style={{ marginTop: 4 }}
                  title="Mark done"
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{f.text}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span className={dueBadgeClass(f.due_at, false)}>
                    {f.due_at ? `Due ${formatDate(f.due_at)}` : "No deadline"}
                  </span>
                  <span>• {f.created_by_name || "—"}</span>
                  <span>• added {formatDate(f.created_at)}</span>
                </div>
              </div>
              {writable && (
                <button
                  className="btn-icon"
                  style={{ color: "var(--danger)", fontSize: 12 }}
                  onClick={() => remove(f)}
                  title="Delete"
                >
                  🗑
                </button>
              )}
            </div>
          ))}

          {done.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", margin: "16px 0 8px" }}>
                COMPLETED ({done.length})
              </div>
              {done.map(f => (
                <div
                  key={f.id}
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "8px 12px",
                    marginBottom: 6,
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    opacity: 0.7,
                    background: "var(--bg-light)",
                  }}
                >
                  {writable && (
                    <input
                      type="checkbox"
                      checked
                      onChange={() => toggleComplete(f)}
                      style={{ marginTop: 4 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, textDecoration: "line-through", color: "var(--text-muted)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{f.text}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                      Completed {formatDate(f.completed_at)}
                    </div>
                  </div>
                  {writable && (
                    <button
                      className="btn-icon"
                      style={{ color: "var(--danger)", fontSize: 12 }}
                      onClick={() => remove(f)}
                    >
                      🗑
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
